/**
 * Simple in-memory cache utility to reduce API calls to Shopify
 * This helps avoid hitting API rate limits
 */

// Cache expiry time in milliseconds (5 minutes)
const CACHE_EXPIRY = 5 * 60 * 1000;

interface CacheItem {
  data: any;
  timestamp: number;
}

// In-memory cache object
const cache: Record<string, CacheItem> = {};

/**
 * Get data from cache if it exists and is not expired
 * @param key Cache key
 * @returns Cached data or null if not found or expired
 */
export const getCachedData = (key: string) => {
  const item = cache[key];
  if (!item) return null;
  
  const now = Date.now();
  if (now - item.timestamp > CACHE_EXPIRY) {
    // Cache expired
    delete cache[key];
    return null;
  }
  
  return item.data;
};

/**
 * Store data in cache
 * @param key Cache key
 * @param data Data to cache
 */
export const setCachedData = (key: string, data: any) => {
  cache[key] = {
    data,
    timestamp: Date.now()
  };
};

/**
 * Clear all cached data
 */
export const clearCache = () => {
  Object.keys(cache).forEach(key => {
    delete cache[key];
  });
};

/**
 * Clear specific cached data by key prefix
 * @param keyPrefix Prefix of keys to clear
 */
export const clearCacheByPrefix = (keyPrefix: string) => {
  Object.keys(cache).forEach(key => {
    if (key.startsWith(keyPrefix)) {
      delete cache[key];
    }
  });
};
